import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

/* ── Lightweight starfield for auth screens ── */
function AuthStarfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    interface Star { x: number; y: number; r: number; a: number; sp: number; ph: number; tw: boolean; }
    const stars: Star[] = [];
    for (let i = 0; i < 160; i++) {
      const bright = Math.random() > 0.78;
      stars.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        r: bright ? Math.random() * 1.1 + 0.5 : Math.random() * 0.6 + 0.2,
        a: bright ? 0.55 + Math.random() * 0.3 : 0.12 + Math.random() * 0.18,
        sp: Math.random() * 0.003 + 0.001, ph: Math.random() * Math.PI * 2, tw: bright,
      });
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        ctx.fillStyle = `rgba(255,255,255,${s.a})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
      }
      return () => window.removeEventListener('resize', resize);
    }

    const animate = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        let alpha = s.a;
        if (s.tw) alpha = s.a * (Math.sin(t * s.sp + s.ph) * 0.4 + 0.6);
        ctx.fillStyle = s.tw
          ? `hsla(${210 + (s.ph * 30) % 60}, 60%, 85%, ${alpha})`
          : `rgba(255,255,255,${alpha})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => { cancelAnimationFrame(rafRef.current); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: [
          'radial-gradient(ellipse 50% 40% at 20% 30%, rgba(79,143,255,0.06), transparent)',
          'radial-gradient(ellipse 40% 35% at 75% 70%, rgba(167,139,250,0.04), transparent)',
        ].join(', '),
      }} />
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
    </>
  );
}

/* ── Keyframes ── */
const STYLE_ID = 'ot-auth-keyframes';
function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes otAuthFadeIn {
      from { opacity: 0; transform: scale(0.96) translateY(8px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes otAuthGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(79,143,255,0.08), 0 0 40px rgba(79,143,255,0.04); }
      50%      { box-shadow: 0 0 24px rgba(79,143,255,0.14), 0 0 48px rgba(79,143,255,0.07); }
    }
  `;
  document.head.appendChild(style);
}

/* ── Shared card style ── */
const cardStyle: React.CSSProperties = {
  background: 'rgba(8, 8, 16, 0.85)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(79, 143, 255, 0.2)',
  borderRadius: 16,
  padding: '40px 36px',
  width: 440,
  maxWidth: '90vw',
  textAlign: 'center',
  position: 'relative',
  boxShadow: '0 0 40px rgba(79, 143, 255, 0.1)',
  animation: 'otAuthFadeIn 0.5s ease-out both, otAuthGlow 4s ease-in-out infinite',
};

/* ── Shared button builder ── */
function neonButton(hovered: boolean, accent = false): React.CSSProperties {
  return {
    width: '100%',
    padding: '13px 24px',
    background: accent
      ? '#4f8fff'
      : hovered ? 'rgba(79,143,255,0.08)' : 'rgba(255,255,255,0.03)',
    border: accent
      ? 'none'
      : `1px solid ${hovered ? 'rgba(79,143,255,0.4)' : 'rgba(79,143,255,0.15)'}`,
    borderRadius: 10,
    color: accent ? '#000000' : '#e6edf3',
    fontSize: 13,
    fontFamily: FONT,
    fontWeight: accent ? 600 : 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    boxShadow: hovered && !accent
      ? '0 0 20px rgba(79,143,255,0.15), 0 0 40px rgba(79,143,255,0.05)'
      : accent
        ? '0 0 16px rgba(79,143,255,0.25)'
        : 'none',
  };
}

interface InviteDetail {
  token: string;
  org_id: string;
  org_name: string;
  github_login: string;
  has_email: boolean;
  org_role: string;
  status: string;
  expires_at: string;
}

/** Refresh JWT so it includes new org memberships, store it, and set the cookie */
async function refreshSession(currentToken: string): Promise<string | null> {
  try {
    const resp = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    if (resp.ok) {
      const data = await resp.json();
      if (data.token) {
        localStorage.setItem('opentool_token', data.token);
        document.cookie = `ot_session=${data.token}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
        return data.token;
      }
    }
  } catch { /* best effort */ }
  return null;
}

export function InviteAccept({ inviteToken }: { inviteToken: string }) {
  const { user, token } = useAuth();
  const [invite, setInvite] = useState<InviteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [acceptedOrgId, setAcceptedOrgId] = useState<string | null>(null);
  const [btnHover, setBtnHover] = useState(false);
  const [email, setEmail] = useState('');
  const [githubHandle, setGithubHandle] = useState('');

  useEffect(() => { ensureKeyframes(); }, []);

  useEffect(() => {
    fetch(`/api/invitations/${inviteToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setInvite(data);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load invitation'); setLoading(false); });
  }, [inviteToken]);

  const isExpired = invite ? new Date(invite.expires_at) < new Date() : false;

  const handleAccept = async () => {
    if (!user && !email.trim()) { setError('Email address is required'); return; }
    setAccepting(true);
    setError(null);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`/api/invitations/${inviteToken}/accept`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: email.trim() || undefined,
          github: githubHandle.trim() || undefined,
        }),
      });
      const data = await resp.json();
      if (data.ok) {
        if (data.token) {
          localStorage.setItem('opentool_token', data.token);
          document.cookie = `ot_session=${data.token}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
        } else if (token) {
          await refreshSession(token);
        }
        setAcceptedOrgId(data.org_id || invite?.org_id || null);
        setAccepted(true);
      } else {
        setError(data.error || 'Failed to accept invitation');
      }
    } catch { setError('Failed to accept invitation'); }
    setAccepting(false);
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: '#000000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT, color: '#484f58', fontSize: 13,
      }}>
        Loading invitation...
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: FONT,
    }}>
      <AuthStarfield />

      <div style={cardStyle}>
        {accepted ? (
          <>
            <div style={{
              fontSize: 40, marginBottom: 16, color: '#4f8fff',
              textShadow: '0 0 20px rgba(79,143,255,0.4)',
            }}>&#10003;</div>
            <h2 style={{
              color: '#4f8fff', fontSize: 20, fontWeight: 600,
              margin: '0 0 8px', fontFamily: FONT,
              textShadow: '0 0 20px rgba(79,143,255,0.3)',
            }}>
              Invitation Accepted
            </h2>
            <p style={{ color: '#8b949e', fontSize: 13, margin: '0 0 28px' }}>
              You are now a member of <span style={{ color: '#e6edf3' }}>{invite?.org_name}</span>
            </p>
            <button
              onClick={() => {
                // Set the active org so the desktop loads into the right context
                if (acceptedOrgId) {
                  localStorage.setItem('opentool_active_org', acceptedOrgId);
                }
                window.location.hash = 'home';
                window.location.reload();
              }}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => setBtnHover(false)}
              style={neonButton(btnHover, true)}
            >
              Go to Dashboard
            </button>
          </>
        ) : error && !invite ? (
          <>
            {/* ToolOS branding */}
            <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              <span style={{ color: '#4f8fff' }}>{'>'}</span>{' '}
              <span style={{ color: '#e6edf3' }}>tool</span>
              <span style={{ color: '#4f8fff' }}>_os</span>
            </div>

            <h2 style={{
              color: '#f87171', fontSize: 20, fontWeight: 600,
              margin: '0 0 12px', fontFamily: FONT,
            }}>
              Invalid Invitation
            </h2>
            <p style={{ color: '#8b949e', fontSize: 13, margin: '0 0 24px' }}>{error}</p>
            <button
              onClick={() => { window.location.hash = ''; window.location.reload(); }}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => setBtnHover(false)}
              style={neonButton(btnHover)}
            >
              Back to Home
            </button>
          </>
        ) : invite ? (
          <>
            {/* ToolOS branding */}
            <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
              <span style={{ color: '#4f8fff' }}>{'>'}</span>{' '}
              <span style={{ color: '#e6edf3' }}>tool</span>
              <span style={{ color: '#4f8fff' }}>_os</span>
            </div>

            <h2 style={{
              color: '#e6edf3', fontSize: 20, fontWeight: 600,
              margin: '0 0 6px', fontFamily: FONT,
            }}>
              Organization Invitation
            </h2>
            <p style={{ color: '#484f58', fontSize: 11, margin: '0 0 28px', letterSpacing: '0.04em' }}>
              You have been invited to join an organization
            </p>

            {/* Invite detail card */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(79, 143, 255, 0.1)',
              borderRadius: 12,
              padding: '18px 24px',
              marginBottom: 24,
              textAlign: 'left',
            }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Organization</div>
                <div style={{ fontSize: 15, color: '#e6edf3', fontWeight: 600 }}>{invite.org_name}</div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role</div>
                <span style={{
                  fontSize: 11, color: '#a78bfa', padding: '3px 10px',
                  background: 'rgba(167,139,250,0.1)', borderRadius: 6,
                  border: '1px solid rgba(167,139,250,0.2)', fontFamily: FONT,
                }}>
                  {invite.org_role}
                </span>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Invited as</div>
                <div style={{ fontSize: 13, color: '#38bdf8' }}>@{invite.github_login}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#484f58', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Expires</div>
                <div style={{ fontSize: 12, color: isExpired ? '#f87171' : '#8b949e' }}>
                  {isExpired ? 'Expired' : new Date(invite.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>

            {invite.status !== 'pending' ? (
              <div style={{
                padding: '14px 18px', background: 'rgba(251,191,36,0.06)',
                border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10,
                marginBottom: 8,
              }}>
                <p style={{ color: '#fbbf24', fontSize: 13, margin: 0, fontFamily: FONT }}>
                  This invitation has already been {invite.status}.
                </p>
                {invite.status === 'accepted' && (
                  <button
                    onClick={() => { window.location.hash = 'home'; window.location.reload(); }}
                    onMouseEnter={() => setBtnHover(true)}
                    onMouseLeave={() => setBtnHover(false)}
                    style={{ ...neonButton(btnHover), marginTop: 14 }}
                  >
                    Go to Dashboard
                  </button>
                )}
              </div>
            ) : isExpired ? (
              <div style={{
                padding: '14px 18px', background: 'rgba(248,113,113,0.06)',
                border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10,
              }}>
                <p style={{ color: '#f87171', fontSize: 13, margin: '0 0 4px', fontFamily: FONT, fontWeight: 600 }}>
                  Invitation Expired
                </p>
                <p style={{ color: '#8b949e', fontSize: 11, margin: 0 }}>
                  This invite link has expired. Please ask the organization admin to send a new one.
                </p>
              </div>
            ) : !user ? (
              /* Unauthenticated: email + optional github form */
              <div>
                <p style={{ color: '#8b949e', fontSize: 12, margin: '0 0 14px', textAlign: 'center' }}>
                  Enter your details to join
                </p>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: '#484f58', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.08em' }}>Email address *</div>
                  <input
                    type="email" required placeholder="you@company.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(79,143,255,0.15)', borderRadius: 8, color: '#e6edf3',
                      fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box' as const,
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.4)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.15)'; }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: '#484f58', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.08em' }}>GitHub handle <span style={{ opacity: 0.5 }}>(optional)</span></div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#484f58', fontSize: 13, pointerEvents: 'none' }}>@</span>
                    <input
                      type="text" placeholder="username"
                      value={githubHandle} onChange={(e) => setGithubHandle(e.target.value.replace(/^@/, ''))}
                      style={{
                        width: '100%', padding: '10px 14px 10px 28px', background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(79,143,255,0.15)', borderRadius: 8, color: '#e6edf3',
                        fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box' as const,
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.4)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.15)'; }}
                    />
                  </div>
                </div>
                <button
                  onClick={handleAccept}
                  disabled={accepting || !email.trim()}
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  style={{
                    ...neonButton(btnHover, true),
                    cursor: accepting ? 'wait' : 'pointer',
                    opacity: !email.trim() ? 0.5 : 1,
                  }}
                >
                  {accepting ? 'Joining...' : 'Accept & Join'}
                </button>
              </div>
            ) : user ? (
              /* Authenticated user: just accept */
              <button
                onClick={handleAccept}
                disabled={accepting}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                style={{
                  ...neonButton(btnHover, true),
                  cursor: accepting ? 'wait' : 'pointer',
                }}
              >
                Sign in to Accept
              </button>
            ) : (
              <button
                onClick={handleAccept}
                disabled={accepting}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                style={{
                  ...neonButton(btnHover, true),
                  cursor: accepting ? 'wait' : 'pointer',
                }}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
              </button>
            )}

            {error && (
              <p style={{
                color: '#f87171', fontSize: 12, marginTop: 14,
                padding: '10px 14px', background: 'rgba(248,113,113,0.08)',
                borderRadius: 10, border: '1px solid rgba(248,113,113,0.2)',
                fontFamily: FONT, lineHeight: 1.5,
              }}>
                {error}
              </p>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
